import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Plus, RefreshCw, X } from "lucide-react";

import EmptyState from "@/components/layout/EmptyState";
import MetricCard from "@/components/layout/MetricCard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type {
  EventLogBreakdownBucket,
  EventLogEntry,
  EventLogFilters,
  EventLogSeriesPoint,
} from "@/lib/api-types";

const currencyFormatter = new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "VND",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("vi-VN");

function parsePositiveInt(value: string | null, fallback: number): number {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return Math.floor(parsed);
}

function formatCompactDate(value?: string): string {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("vi-VN", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatMetricValue(value: number, metric: "count" | "amount" | "quantity"): string {
  if (metric === "amount") {
    return currencyFormatter.format(value);
  }
  return numberFormatter.format(value);
}

function readLabels(searchParams: URLSearchParams): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    if (!key.startsWith("label.")) {
      continue;
    }
    const labelKey = key.slice("label.".length).trim();
    if (!labelKey || !value.trim()) {
      continue;
    }
    labels[labelKey] = value.trim();
  }
  return labels;
}

function describeParty(entry: EventLogEntry["actor"] | EventLogEntry["target"]): string {
  if (entry.label && entry.id) {
    return `${entry.label} (${entry.id})`;
  }
  return entry.label || entry.id || "N/A";
}

function normalizeBreakdown(
  value: EventLogBreakdownBucket[] | undefined,
): EventLogBreakdownBucket[] {
  return Array.isArray(value) ? value : [];
}

function normalizeSeries(value: EventLogSeriesPoint[] | undefined): EventLogSeriesPoint[] {
  return Array.isArray(value) ? value : [];
}

function BreakdownList({
  title,
  items,
  metric,
}: {
  title: string;
  items: EventLogBreakdownBucket[];
  metric: "count" | "amount" | "quantity";
}) {
  const maxValue = items.reduce((current, item) => Math.max(current, item.value), 0);

  return (
    <Card className="border-border/80 bg-card/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <EmptyState
            title="Chưa có dữ liệu"
            description="Breakdown sẽ hiện khi event stream có bản ghi khớp bộ lọc đang chọn."
            className="p-6"
          />
        ) : (
          items.map((item) => {
            const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 6) : 0;
            return (
              <div key={`${title}-${item.key}`} className="space-y-2 rounded-2xl border border-border/80 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{item.label}</span>
                  <span className="text-muted-foreground">
                    {formatMetricValue(item.value, metric)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary/80" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function SeriesList({
  points,
  metric,
}: {
  points: EventLogSeriesPoint[];
  metric: "count" | "amount" | "quantity";
}) {
  const maxValue = points.reduce((current, point) => Math.max(current, point.value), 0);

  return (
    <Card className="border-border/80 bg-card/80">
      <CardHeader>
        <CardTitle>Timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {points.length === 0 ? (
          <EmptyState
            title="Chưa có dữ liệu timeline"
            description="Series API hiện không trả bucket nào cho bộ lọc đang chọn."
            className="p-6"
          />
        ) : (
          points.map((point) => {
            const width = maxValue > 0 ? Math.max((point.value / maxValue) * 100, 4) : 0;
            return (
              <div
                key={`${point.bucket}-${point.group || "all"}`}
                className="space-y-2 rounded-2xl border border-border/80 bg-muted/20 p-3"
              >
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{point.bucket}</p>
                    {point.group ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {point.group}
                      </p>
                    ) : null}
                  </div>
                  <span className="text-muted-foreground">
                    {formatMetricValue(point.value, metric)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary/80" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default function EventLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [labelKeyDraft, setLabelKeyDraft] = useState("");
  const [labelValueDraft, setLabelValueDraft] = useState("");

  const labels = useMemo(() => readLabels(searchParams), [searchParams]);
  const seriesMetric = (searchParams.get("seriesMetric") || "count") as
    | "count"
    | "amount"
    | "quantity";
  const seriesInterval = (searchParams.get("seriesInterval") || "day") as
    | "hour"
    | "day"
    | "month";
  const seriesGroupBy = searchParams.get("seriesGroupBy") || "";

  const filters = useMemo<EventLogFilters>(
    () => ({
      search: searchParams.get("search") ?? "",
      source: searchParams.get("source") ?? "",
      category: searchParams.get("category") ?? "",
      action: searchParams.get("action") ?? "",
      eventName: searchParams.get("eventName") ?? "",
      labels,
      actorId: searchParams.get("actorId") ?? "",
      targetId: searchParams.get("targetId") ?? "",
      itemCode: searchParams.get("itemCode") ?? "",
      currency: searchParams.get("currency") ?? "",
      status: searchParams.get("status") ?? "",
      resource: searchParams.get("resource") ?? "",
      dateFrom: searchParams.get("dateFrom") ?? "",
      dateTo: searchParams.get("dateTo") ?? "",
      page: parsePositiveInt(searchParams.get("page"), 1),
      limit: 50,
    }),
    [labels, searchParams],
  );

  const updateQuery = (
    updates: Record<string, string | number | undefined>,
    options: { resetPage?: boolean; replace?: boolean } = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    if (options.resetPage) {
      next.delete("page");
    }
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === "") {
        next.delete(key);
        return;
      }
      next.set(key, String(value));
    });
    setSearchParams(next, { replace: options.replace ?? true });
  };

  const addLabelFilter = () => {
    const nextKey = labelKeyDraft.trim().toLowerCase();
    const nextValue = labelValueDraft.trim();
    if (!nextKey || !nextValue) {
      return;
    }
    updateQuery({ [`label.${nextKey}`]: nextValue }, { resetPage: true });
    setLabelKeyDraft("");
    setLabelValueDraft("");
  };

  const removeLabelFilter = (key: string) => {
    updateQuery({ [`label.${key}`]: undefined }, { resetPage: true });
  };

  const overviewQuery = useQuery({
    queryKey: ["event-logs-overview", filters],
    queryFn: () => api.getEventLogOverview(filters),
  });

  const eventsQuery = useQuery({
    queryKey: ["event-logs-events", filters],
    queryFn: () => api.getEventLogEvents(filters),
  });

  const topCategoriesQuery = useQuery({
    queryKey: ["event-logs-breakdown", filters, "category", "count"],
    queryFn: () => api.getEventLogBreakdown(filters, "category", "count", 8),
  });

  const topActionsQuery = useQuery({
    queryKey: ["event-logs-breakdown", filters, "action", "count"],
    queryFn: () => api.getEventLogBreakdown(filters, "action", "count", 8),
  });

  const topResourcesQuery = useQuery({
    queryKey: ["event-logs-breakdown", filters, "resource", "count"],
    queryFn: () => api.getEventLogBreakdown(filters, "resource", "count", 8),
  });

  const seriesQuery = useQuery({
    queryKey: ["event-logs-series", filters, seriesMetric, seriesInterval, seriesGroupBy],
    queryFn: () =>
      api.getEventLogSeries(filters, seriesMetric, seriesInterval, seriesGroupBy || undefined),
  });

  const activeQueryCount =
    Object.entries(filters).filter(([key, value]) => {
      if (key === "page" || key === "limit" || key === "labels") {
        return false;
      }
      return value !== undefined && value !== null && value !== "";
    }).length + Object.keys(labels).length;

  const overview = overviewQuery.data ?? {
    totalEvents: 0,
    totalAmount: 0,
    totalQuantity: 0,
    uniqueActors: 0,
    uniqueTargets: 0,
    firstOccurred: undefined,
    lastOccurred: undefined,
  };
  const categoryItems = normalizeBreakdown(topCategoriesQuery.data);
  const actionItems = normalizeBreakdown(topActionsQuery.data);
  const resourceItems = normalizeBreakdown(topResourcesQuery.data);
  const seriesPoints = normalizeSeries(seriesQuery.data);
  const isRefreshing =
    overviewQuery.isFetching ||
    eventsQuery.isFetching ||
    topCategoriesQuery.isFetching ||
    topActionsQuery.isFetching ||
    topResourcesQuery.isFetching ||
    seriesQuery.isFetching;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Observability"
        title="Event logs"
        description={
          activeQueryCount > 0
            ? `${numberFormatter.format(activeQueryCount)} filter đang hoạt động. Trang này xử lý ổn định cả khi breakdown hoặc series chưa có dữ liệu.`
            : "Lọc event stream theo labels, actor, resource và khoảng thời gian mà không làm vỡ UI khi API trả về tập rỗng."
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                void Promise.all([
                  overviewQuery.refetch(),
                  eventsQuery.refetch(),
                  topCategoriesQuery.refetch(),
                  topActionsQuery.refetch(),
                  topResourcesQuery.refetch(),
                  seriesQuery.refetch(),
                ]);
              }}
              disabled={isRefreshing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
              Reload
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
            >
              Reset
            </Button>
          </>
        }
      />

      <Card className="border-border/80 bg-card/80">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <Input
              placeholder="Search"
              value={filters.search || ""}
              onChange={(event) => updateQuery({ search: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Category"
              value={filters.category || ""}
              onChange={(event) => updateQuery({ category: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Action"
              value={filters.action || ""}
              onChange={(event) => updateQuery({ action: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Resource"
              value={filters.resource || ""}
              onChange={(event) => updateQuery({ resource: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Status"
              value={filters.status || ""}
              onChange={(event) => updateQuery({ status: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Actor ID"
              value={filters.actorId || ""}
              onChange={(event) => updateQuery({ actorId: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Target ID"
              value={filters.targetId || ""}
              onChange={(event) => updateQuery({ targetId: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Item code"
              value={filters.itemCode || ""}
              onChange={(event) => updateQuery({ itemCode: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Currency"
              value={filters.currency || ""}
              onChange={(event) => updateQuery({ currency: event.target.value }, { resetPage: true })}
            />
            <Input
              placeholder="Event name"
              value={filters.eventName || ""}
              onChange={(event) => updateQuery({ eventName: event.target.value }, { resetPage: true })}
            />
            <Input
              type="date"
              value={filters.dateFrom || ""}
              onChange={(event) => updateQuery({ dateFrom: event.target.value }, { resetPage: true })}
            />
            <Input
              type="date"
              value={filters.dateTo || ""}
              onChange={(event) => updateQuery({ dateTo: event.target.value }, { resetPage: true })}
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={seriesMetric}
              onChange={(event) => updateQuery({ seriesMetric: event.target.value }, { replace: true })}
            >
              <option value="count">Series: Count</option>
              <option value="amount">Series: Amount</option>
              <option value="quantity">Series: Quantity</option>
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={seriesInterval}
              onChange={(event) => updateQuery({ seriesInterval: event.target.value }, { replace: true })}
            >
              <option value="hour">Interval: Hour</option>
              <option value="day">Interval: Day</option>
              <option value="month">Interval: Month</option>
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={seriesGroupBy}
              onChange={(event) => updateQuery({ seriesGroupBy: event.target.value }, { replace: true })}
            >
              <option value="">Group: None</option>
              <option value="category">Group: Category</option>
              <option value="action">Group: Action</option>
              <option value="resource">Group: Resource</option>
              <option value="status">Group: Status</option>
              <option value="currency">Group: Currency</option>
            </select>
          </div>

          <div className="rounded-2xl border border-border/80 bg-muted/20 p-3">
            <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
              <Input
                placeholder="Label key"
                value={labelKeyDraft}
                onChange={(event) => setLabelKeyDraft(event.target.value)}
              />
              <Input
                placeholder="Label value"
                value={labelValueDraft}
                onChange={(event) => setLabelValueDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addLabelFilter();
                  }
                }}
              />
              <Button type="button" onClick={addLabelFilter}>
                <Plus className="mr-2 h-4 w-4" />
                Add label
              </Button>
            </div>
            {Object.keys(labels).length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(labels).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => removeLabelFilter(key)}
                    className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-background px-3 py-1 text-xs"
                  >
                    <span>
                      {key}={value}
                    </span>
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          label="Total events"
          value={numberFormatter.format(overview.totalEvents)}
          hint="Tổng số bản ghi khớp bộ lọc hiện tại."
        />
        <MetricCard
          label="Total amount"
          value={currencyFormatter.format(overview.totalAmount)}
          hint="Áp dụng cho các event có amount."
        />
        <MetricCard
          label="Total quantity"
          value={numberFormatter.format(overview.totalQuantity)}
          hint="Tổng quantity từ item payload."
        />
        <MetricCard
          label="Unique actors"
          value={numberFormatter.format(overview.uniqueActors)}
          hint={`Unique targets ${numberFormatter.format(overview.uniqueTargets)}`}
        />
        <MetricCard
          label="Last event"
          value={formatCompactDate(overview.lastOccurred)}
          hint={`First event ${formatCompactDate(overview.firstOccurred)}`}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <SeriesList points={seriesPoints} metric={seriesMetric} />
        <BreakdownList title="Top categories" items={categoryItems} metric="count" />
        <BreakdownList title="Top actions" items={actionItems} metric="count" />
        <BreakdownList title="Top resources" items={resourceItems} metric="count" />
      </div>

      <Card className="border-border/80 bg-card/80">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Events</CardTitle>
          <div className="text-sm text-muted-foreground">
            {eventsQuery.data ? numberFormatter.format(eventsQuery.data.pagination.total) : "0"} records
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {eventsQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading logs...</div>
          ) : eventsQuery.isError || !eventsQuery.data ? (
            <div className="text-sm text-red-500">Không tải được event logs.</div>
          ) : eventsQuery.data.items.length === 0 ? (
            <EmptyState
              title="Không có event nào"
              description="Bộ lọc hiện tại không khớp bản ghi nào, nhưng UI vẫn giữ ổn định để bạn tiếp tục điều chỉnh filter."
            />
          ) : (
            <>
              <div className="space-y-3">
                {eventsQuery.data.items.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-border/80 bg-muted/20 p-4"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded-full border border-border/80 bg-background px-2 py-1">
                            {item.category || "misc"}
                          </span>
                          <span className="rounded-full border border-border/80 bg-background px-2 py-1">
                            {item.action || "event"}
                          </span>
                          {item.resource ? (
                            <span className="rounded-full border border-border/80 bg-background px-2 py-1">
                              {item.resource}
                            </span>
                          ) : null}
                          {item.status ? (
                            <span className="rounded-full border border-border/80 bg-background px-2 py-1">
                              {item.status}
                            </span>
                          ) : null}
                        </div>
                        <div>
                          <p className="font-semibold">{item.eventName}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatCompactDate(item.occurredAt)}
                          </p>
                        </div>
                      </div>

                      <div className="text-left lg:text-right">
                        {item.amount !== undefined ? (
                          <p className="text-lg font-semibold text-emerald-600">
                            {currencyFormatter.format(item.amount)}
                          </p>
                        ) : item.item.quantity !== undefined ? (
                          <p className="text-lg font-semibold">
                            {numberFormatter.format(item.item.quantity)}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">No metric</p>
                        )}
                        {item.currency ? (
                          <p className="text-xs text-muted-foreground">{item.currency}</p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm lg:grid-cols-3">
                      <div className="rounded-2xl border border-border/80 bg-background/60 p-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                          Actor
                        </p>
                        <p className="mt-1 font-medium">{describeParty(item.actor)}</p>
                      </div>
                      <div className="rounded-2xl border border-border/80 bg-background/60 p-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                          Target
                        </p>
                        <p className="mt-1 font-medium">{describeParty(item.target)}</p>
                      </div>
                      <div className="rounded-2xl border border-border/80 bg-background/60 p-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                          Item
                        </p>
                        <p className="mt-1 font-medium">
                          {item.item.name || item.item.code || "N/A"}
                        </p>
                        {item.item.quantity !== undefined ? (
                          <p className="text-xs text-muted-foreground">
                            Qty {numberFormatter.format(item.item.quantity)}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {item.labels && Object.keys(item.labels).length > 0 ? (
                      <details className="mt-3 rounded-2xl border border-border/80 bg-background/60 p-3 text-sm">
                        <summary className="cursor-pointer font-medium">Labels</summary>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Object.entries(item.labels).map(([key, value]) => (
                            <span
                              key={`${item.id}-${key}`}
                              className="rounded-full border border-border/80 bg-muted/20 px-2 py-1 text-xs"
                            >
                              {key}={value}
                            </span>
                          ))}
                        </div>
                      </details>
                    ) : null}

                    {item.metadata && Object.keys(item.metadata).length > 0 ? (
                      <details className="mt-3 rounded-2xl border border-border/80 bg-background/60 p-3 text-sm">
                        <summary className="cursor-pointer font-medium">Metadata</summary>
                        <pre className="mt-3 overflow-x-auto text-xs text-muted-foreground">
                          {JSON.stringify(item.metadata, null, 2)}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border/80 pt-4">
                <Button
                  variant="outline"
                  disabled={!eventsQuery.data.pagination.hasPrev}
                  onClick={() =>
                    updateQuery({ page: Math.max((filters.page || 1) - 1, 1) }, { replace: false })
                  }
                >
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {eventsQuery.data.pagination.page} / {eventsQuery.data.pagination.totalPages}
                </div>
                <Button
                  variant="outline"
                  disabled={!eventsQuery.data.pagination.hasNext}
                  onClick={() =>
                    updateQuery({ page: (filters.page || 1) + 1 }, { replace: false })
                  }
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
